import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import promotionsApi from 'api/requests/promotions';
import { useUser } from 'context/user/User';
import currency from 'helpers/currency';
import { Tag } from 'lucide-react';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const TYPE_LABELS = {
    percentage:  'Percentual',
    fixed:       'Valor fixo',
    fixed_price: 'Preço fixo',
    bundle:      'Combo',
};

export default function PromotionsStatsTab({ period, customStart, customEnd }) {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!slug) return;
        if (period === 'custom' && (!customStart || !customEnd)) return;
        load();
    }, [slug, period, customStart, customEnd]);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const params = period === 'custom'
                ? { start_date: customStart, end_date: customEnd }
                : { period };
            const res = await promotionsApi.stats(slug, params);
            setData(res.data.data || res.data || []);
        } catch {
            setError('Erro ao carregar estatísticas de promoções.');
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <LoadingWrap>
                <Spinner />
            </LoadingWrap>
        );
    }

    if (error) {
        return (
            <ErrorWrap>
                <ErrorText>{error}</ErrorText>
                <RetryBtn onClick={load}>Tentar novamente</RetryBtn>
            </ErrorWrap>
        );
    }

    if (!data.length) {
        return (
            <EmptyState>
                <EmptyIcon><Tag size={28} color={panelAccent(colors.occasionalPurple)} /></EmptyIcon>
                <EmptyTitle>Nenhuma promoção aplicada</EmptyTitle>
                <EmptyHint>Dados aparecerão aqui quando promoções forem usadas no período selecionado</EmptyHint>
            </EmptyState>
        );
    }

    return (
        <Container>
            <TableWrapper>
                <Table>
                    <thead>
                        <tr>
                            <Th>Promoção</Th>
                            <Th>Tipo</Th>
                            <Th $right>Desconto Total</Th>
                            <Th $right>Itens Vendidos</Th>
                            <Th $right>Unidades Grátis</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map(row => (
                            <Tr key={row.promotion_id}>
                                <Td>
                                    <PromoName>{row.name}</PromoName>
                                </Td>
                                <Td>
                                    <TypeText>{TYPE_LABELS[row.type] || row.type}</TypeText>
                                </Td>
                                <Td $right>
                                    <DiscountValue>{currency.format(row.total_discount_given)}</DiscountValue>
                                </Td>
                                <Td $right>
                                    <NumValue>{row.items_sold_under_promotion}</NumValue>
                                </Td>
                                <Td $right>
                                    <NumValue>
                                        {row.type === 'bundle' ? row.free_units_given : '—'}
                                    </NumValue>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
            </TableWrapper>
        </Container>
    );
}

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Container = styled.div``;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 80px 0;
`;

const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid #eee;
    border-top-color: ${panelAccent(colors.occasionalPurple)};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const ErrorWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 0;
`;

const ErrorText = styled.p`color: ${colors.danger}; font-size: 14px; margin: 0;`;

const RetryBtn = styled.button`
    font-size: 13px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    background: none;
    border: 1.5px solid ${panelAccent(colors.occasionalPurple)};
    border-radius: 8px;
    padding: 8px 20px;
    cursor: pointer;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    text-align: center;
    gap: 10px;
`;

const EmptyIcon = styled.div`
    width: 52px; height: 52px; border-radius: 50%;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex; align-items: center; justify-content: center;
`;

const EmptyTitle = styled.p`font-weight: 600; font-size: 16px; color: ${colors.nightDark}; margin: 0;`;
const EmptyHint = styled.p`font-size: 13px; color: ${colors.nightLight}; margin: 0; max-width: 320px;`;

const TableWrapper = styled.div`overflow-x: auto;`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-align: ${({ $right }) => $right ? 'right' : 'left'};
    border-bottom: 1px solid #f0f0f0;
    white-space: nowrap;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #f5f5f5;
    &:last-child { border-bottom: none; }
    &:hover { background: ${colors.neutral}80; }
`;

const Td = styled.td`
    padding: 12px 16px;
    vertical-align: middle;
    text-align: ${({ $right }) => $right ? 'right' : 'left'};
`;

const PromoName = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark};`;
const TypeText = styled.span`font-size: 13px; color: ${colors.night};`;
const DiscountValue = styled.span`font-size: 14px; font-weight: 600; color: ${colors.occasionalGreen}; font-variant-numeric: tabular-nums;`;
const NumValue = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark}; font-variant-numeric: tabular-nums;`;
